export const MOBILE_BREAKPOINT_QUERY = '(max-width: 767px)'

const HIGH_PERFORMANCE_HOST = 'https://www.highperformanceformat.com'
const INTERVENTION_HOST = 'https://interventioncopiedloitering.com'

// Each Adsterra key belongs to one fixed banner size.
export const ADSTERRA_BANNER_UNITS = {
  '728x90': { key: '56c5a29c65326aee523801f3d2dbecf3', host: INTERVENTION_HOST },
  '468x60': { key: '75be020c1ae129e2ba977bc5f187ee4e', host: HIGH_PERFORMANCE_HOST },
  '320x50': { key: 'df819f833226f9abcd17e2481bc379fa', host: HIGH_PERFORMANCE_HOST },
  '300x250': { key: '6a9ae8f3b710012a69a6247ef2ec7d63', host: INTERVENTION_HOST },
}

export const ADSTERRA_NATIVE_PLACEMENTS = {
  learningNativeAfterCode: {
    key: 'eeac07d83aba119ec90085bf2294e5e8',
    containerId: 'container-eeac07d83aba119ec90085bf2294e5e8',
    src: `${INTERVENTION_HOST}/eeac07d83aba119ec90085bf2294e5e8/invoke.js`,
    layout: '4:1',
    fontSize: 'inherit',
    fontColor: 'inherit',
    refreshMs: 120 * 1000,
    initialDelayMs: 2500,
    requestTimeoutMs: 45 * 1000,
    lazy: true,
    requireRecentActivity: true,
    activityWindowMs: 120 * 1000,
  },
}

const GAM_NETWORK_CODE = '23360249177'

const gamPath = (adUnitCode) => `/${GAM_NETWORK_CODE}/${adUnitCode}`

const nexoPath = value => /^\/\d+\/[a-zA-Z0-9_/-]+$/.test(String(value || '')) ? value : ''
const nexoEnabled = /^(1|true|on|yes)$/i.test(import.meta.env.VITE_NEXO_ADS_ENABLED || '')
const adaptiveSeoPath = /^(1|true|on|yes)$/i.test(import.meta.env.VITE_SEO_ADAPTIVE_ADS_ENABLED || '')
  ? nexoPath(import.meta.env.VITE_GAM_SEO_CONTENT_ADAPTIVE_AD_UNIT_PATH)
  : ''
const seoVariants = adaptiveSeoPath ? {
  wide: { adUnitPath: adaptiveSeoPath, width: 728, height: 90, sizes: [[728, 90], [468, 60], [320, 50], [300, 50]] },
  desktop: { adUnitPath: adaptiveSeoPath, width: 468, height: 60, sizes: [[468, 60], [320, 50], [300, 50]] },
  mobile: { adUnitPath: adaptiveSeoPath, width: 320, height: 50, sizes: [[320, 50], [300, 50]] },
  compact: { adUnitPath: adaptiveSeoPath, width: 300, height: 50, sizes: [[300, 50]] },
} : {
  desktop: { adUnitPath: gamPath('visualg_seo_content_desktop'), width: 468, height: 60 },
  mobile: { adUnitPath: gamPath('visualg_seo_content_mobile'), width: 320, height: 50 },
}

export const GOOGLE_AD_MANAGER_PLACEMENTS = {
  nexoLeft: { lazy: true, responseDeadlineMs: 12000, variants: { desktop: { adUnitPath: nexoEnabled ? nexoPath(import.meta.env.VITE_GAM_NEXO_LEFT_AD_UNIT_PATH) : '', width: 300, height: 250 } } },
  nexoRight: { lazy: true, responseDeadlineMs: 12000, variants: { desktop: { adUnitPath: nexoEnabled ? nexoPath(import.meta.env.VITE_GAM_NEXO_RIGHT_AD_UNIT_PATH) : '', width: 300, height: 250 } } },
  nexoNavbar: { lazy: false, responseDeadlineMs: 12000, variants: { desktop: { adUnitPath: nexoEnabled ? nexoPath(import.meta.env.VITE_GAM_NEXO_NAVBAR_AD_UNIT_PATH) : '', width: 468, height: 60 } } },
  navbar: {
    initialDelayMs: 0,
    lazy: false,
    requireRecentActivity: false,
    responseDeadlineMs: 12000,
    noFillRefreshMs: 60000,
    variants: {
      desktop: {
        adUnitPath: gamPath('visualg_navbar_desktop'),
        width: 728,
        height: 90,
        sizes: [[728, 90], [468, 60]],
        preferPrimarySize: true,
      },
      compact: {
        adUnitPath: gamPath('visualg_navbar_desktop'),
        width: 468,
        height: 60,
      },
    },
  },
  seoContent: {
    initialDelayMs: 0,
    lazy: true,
    requireRecentActivity: false,
    responseDeadlineMs: 12000,
    // Fits the existing 90px editorial frame. Activation requires a real GAM
    // unit configured with these sizes; legacy units are never redefined.
    preferredMode: adaptiveSeoPath ? 'wide' : undefined,
    variants: seoVariants,
  },
  modalBottom: {
    initialDelayMs: 0,
    lazy: false,
    requireRecentActivity: false,
    responseDeadlineMs: 12000,
    variants: {
      desktop: {
        adUnitPath: gamPath('visualg_modal_bottom_desktop'),
        width: 728,
        height: 90,
      },
      mobile: {
        adUnitPath: gamPath('visualg_modal_bottom_mobile'),
        width: 300,
        height: 250,
      },
    },
  },
  learningAfterCode: {
    initialDelayMs: 0,
    // The same editorial lesson can be read on /aprender or inside the editor.
    pageFamily: 'learning',
    lazy: true,
    requireRecentActivity: false,
    responseDeadlineMs: 12000,
    variants: {
      desktop: {
        adUnitPath: gamPath('visualg_learn_after_code_desktop'),
        width: 728,
        height: 90,
      },
      mobile: {
        adUnitPath: gamPath('visualg_learn_after_code_mobile'),
        width: 300,
        height: 250,
      },
    },
  },
}

export function adsterraPlacementConfig(config) {
  if (!config) return undefined
  const variants = Object.fromEntries(Object.entries(config.variants).flatMap(([mode, variant]) => {
    const unit = ADSTERRA_BANNER_UNITS[`${variant.width}x${variant.height}`]
    return unit ? [[mode, { ...variant, ...unit }]] : []
  }))
  // Filled banners are never refreshed by the legacy load-cycle timer.
  return { ...config, refreshMs: 0, variants }
}

export const ADSTERRA_DISPLAY_PLACEMENTS = Object.fromEntries(
  Object.entries(GOOGLE_AD_MANAGER_PLACEMENTS).map(([name, config]) => [name, adsterraPlacementConfig(config)]),
)

// Keep the shared layout even if only Adsterra has a configured unit.
export function resolveAdPlacementConfig(placement, customConfig, adsterraEnabled = false) {
  const config = customConfig || GOOGLE_AD_MANAGER_PLACEMENTS[placement]
  if (!adsterraEnabled || !config || (placement.startsWith('nexo') && !nexoEnabled)) return config
  const adsterra = customConfig ? adsterraPlacementConfig(config) : ADSTERRA_DISPLAY_PLACEMENTS[placement]
  return { ...config, variants: Object.fromEntries(Object.entries(config.variants).map(([mode, variant]) => [mode, adsterra?.variants[mode] || variant])) }
}

export const resolveCommonModalAdState = () => ({ active: false, source: null })
